import React, { useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, SkipForward, SkipBack, Volume2 } from "lucide-react";
import { useSettings } from "@/hooks/useSettings";
import { useSpotifyAuth } from "@/hooks/useSpotifyAuth";
import { useSpotifyPlayer } from "@/hooks/useSpotifyPlayer";
import { startPlayback } from "@/lib/spotify/api";

const SpotifyPlayer: React.FC = () => {
  const { settings, setSound } = useSettings();
  const { isAuthenticated, getValidToken } = useSpotifyAuth();
  const isActive = settings.sound.type === "spotify" && isAuthenticated;
  const { deviceId, track, isReady, error, togglePlay, skipNext, skipPrev, setVolume } =
    useSpotifyPlayer(isActive);

  // Start playback when device is ready and playlist is selected
  useEffect(() => {
    if (!isReady || !deviceId) return;
    const uri = settings.sound.spotify.selectedPlaylistUri;
    if (!uri) return;

    const play = async () => {
      try {
        const token = await getValidToken();
        await startPlayback(token, deviceId, uri);
      } catch {
        // Playback errors handled by SDK state
      }
    };
    void play();
    // Only trigger when device becomes ready or playlist changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, deviceId, settings.sound.spotify.selectedPlaylistUri]);

  // Sync volume from settings
  useEffect(() => {
    if (isReady) setVolume(settings.sound.volume);
  }, [settings.sound.volume, isReady, setVolume]);

  // Listen for global toggle event
  const handleToggle = useCallback(() => {
    if (settings.sound.type !== "spotify") return;
    togglePlay();
  }, [settings.sound.type, togglePlay]);

  useEffect(() => {
    window.addEventListener("uwu:toggle-sound", handleToggle);
    return () => window.removeEventListener("uwu:toggle-sound", handleToggle);
  }, [handleToggle]);

  if (settings.sound.type !== "spotify") return null;

  if (!isAuthenticated) {
    return (
      <div className="fixed bottom-4 left-4 z-50 bg-background/80 backdrop-blur-sm border border-border rounded-lg p-4 shadow-lg">
        <p className="text-sm text-muted-foreground">
          Connect to Spotify in Settings
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed bottom-4 left-4 z-50 bg-background/80 backdrop-blur-sm border border-border rounded-lg p-4 shadow-lg max-w-xs">
        <p className="text-sm text-destructive">{error}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-2 w-full"
          onClick={() => setSound({ type: "none" })}
        >
          Dismiss
        </Button>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="fixed bottom-4 left-4 z-50 bg-background/80 backdrop-blur-sm border border-border rounded-lg p-4 shadow-lg">
        <div className="flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
          <span className="text-sm text-muted-foreground">Connecting to Spotify...</span>
        </div>
      </div>
    );
  }

  const handleVolumeChange = (v: number[]) => {
    setSound({ volume: v[0] });
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 bg-background/80 backdrop-blur-sm border border-border rounded-lg p-4 shadow-lg max-w-xs">
      {track ? (
        <div className="flex items-center gap-3">
          {track.albumArt && (
            <img
              src={track.albumArt}
              alt={track.name}
              className="h-12 w-12 rounded object-cover flex-shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{track.name}</p>
            <p className="text-xs text-muted-foreground truncate">{track.artist}</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mb-2">
          {settings.sound.spotify.selectedPlaylistName ?? "No playlist selected"}
        </p>
      )}

      <div className="flex items-center gap-1 mt-2">
        <Button variant="ghost" size="sm" onClick={skipPrev} className="h-8 w-8 p-0">
          <SkipBack className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={togglePlay} className="h-8 w-8 p-0">
          {track?.paused !== false ? (
            <Play className="h-4 w-4" />
          ) : (
            <Pause className="h-4 w-4" />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={skipNext} className="h-8 w-8 p-0">
          <SkipForward className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2 ml-2 flex-1 min-w-[80px]">
          <Volume2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Slider
            value={[settings.sound.volume]}
            onValueChange={handleVolumeChange}
            max={100}
            min={0}
            step={1}
            className="flex-1"
          />
        </div>
      </div>

      <div className="flex items-center justify-center gap-1 mt-2">
        <svg viewBox="0 0 24 24" className="h-3 w-3 fill-[#1DB954]">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
        </svg>
        <span className="text-[10px] text-muted-foreground">Spotify</span>
      </div>
    </div>
  );
};

export default SpotifyPlayer;
